/**
 * AI Report Actions Service
 * Provides AI-compatible actions for report generation
 * IMPORTANT: Supports Hospital, Community Transfer, Deceased, Admission, and Overall pages
 * Supports generating PDF reports, Excel reports, and automated reports
 */

import { HOSPITAL_CARDS_TYPE, HOSPITAL_CARDS_LABELS } from '../types/hospital.type';
import { CO_TRANSFER_CARDS_TYPE, COMMUNITY_CARD_LABELS } from '../types/community-transfer.type';
import { DECEASED_CARDS_TYPE, DECEASED_CARDS_LABELS } from '../types/deceased.type';
import { ADMISSION_CARDS_TYPE, ADMISSION_CARDS_LABELS } from '../types/admission.type';
import { OVERALL_CARDS_TYPE, OVERALL_CARDS_LABELS } from '../types/overall.type';
import { AUTOMATICALLY_REPORT_TYPE, REPORT_FILE_TYPE } from '../types/report.type';
import { PAGE_TYPE } from '../types/pages.type';
import logger from '../utilis/logger';

class AiReportActionsService {
    constructor() {
        this.handleOnClickReport = null;
        this.downloadExcel = null;
        this.getState = null;
    }

    /**
     * Initialize the service with required functions
     * @param {Function} handleOnClickReport - Report generation function
     * @param {Function} downloadExcel - Excel download function
     * @param {Function} getState - Function to get current state
     */
    initialize(handleOnClickReport, downloadExcel, getState) {
        this.handleOnClickReport = handleOnClickReport;
        this.downloadExcel = downloadExcel;
        this.getState = getState;
        logger.log('🔧 AI Report Actions Service initialized');
    }

    /**
     * Detect current page type from state
     * @returns {string} Page type
     */
    getCurrentPageType() {
        if (!this.getState) {
            return PAGE_TYPE.HOSPITAL;
        }

        const state = this.getState();
        
        // Check for community transfer specific state
        if (state.communityTransfer && Object.keys(state.communityTransfer).length > 0) {
            return PAGE_TYPE.COMMUNITY_TRANSFER;
        }
        
        // Check for deceased specific state
        if (state.deceased && Object.keys(state.deceased).length > 0) {
            return PAGE_TYPE.DECEASED;
        }
        
        // Check for overall specific state
        if (state.overall && Object.keys(state.overall).length > 0) {
            return PAGE_TYPE.OVERALL;
        }
        
        // Check for admission specific state
        if (state.admission && Object.keys(state.admission).length > 0) {
            return PAGE_TYPE.ADMISSION;
        }
        
        // Default to hospital
        return PAGE_TYPE.HOSPITAL;
    }

    /**
     * Get available report types and options
     * IMPORTANT: Now page-aware - returns correct data based on current page
     * @returns {Object} Available report options
     */
    getAvailableReportOptions() {
        if (!this.getState) return {};

        const state = this.getState();
        const pageType = this.getCurrentPageType();
        const isCommunityPage = pageType === PAGE_TYPE.COMMUNITY_TRANSFER;
        const isDeceasedPage = pageType === PAGE_TYPE.DECEASED;
        const isOverallPage = pageType === PAGE_TYPE.OVERALL;
        const isAdmissionPage = pageType === PAGE_TYPE.ADMISSION;
        
        // Get data from correct state based on page type
        const pageState = isCommunityPage ? state.communityTransfer : 
                        isDeceasedPage ? state.deceased : 
                        isOverallPage ? state.overall :
                        isAdmissionPage ? state.admission :
                        state.hospital;
        const { filterListData, cardFilter, transferType } = pageState || {};

        return {
            reportTypes: {
                pdf: {
                    type: REPORT_FILE_TYPE.PDF,
                    label: 'PDF Report',
                    description: 'Generate a PDF report with charts and data',
                    available: true
                },
                excel: {
                    type: REPORT_FILE_TYPE.EXCEL,
                    label: 'Excel Report',
                    description: 'Generate an Excel spreadsheet with detailed data',
                    available: true
                }
            },
            automatedReportTypes: {
                daily: {
                    type: AUTOMATICALLY_REPORT_TYPE.DAILY,
                    label: 'Daily Automated Report',
                    description: 'Send report automatically every day'
                },
                weekly: {
                    type: AUTOMATICALLY_REPORT_TYPE.WEEKLY,
                    label: 'Weekly Automated Report',
                    description: 'Send report automatically every week'
                },
                monthly: {
                    type: AUTOMATICALLY_REPORT_TYPE.MONTHLY,
                    label: 'Monthly Automated Report',
                    description: 'Send report automatically every month'
                },
                quarterly: {
                    type: AUTOMATICALLY_REPORT_TYPE.QUARTERLY,
                    label: 'Quarterly Automated Report',
                    description: 'Send report automatically every quarter'
                },
                yearly: {
                    type: AUTOMATICALLY_REPORT_TYPE.YEARLY,
                    label: 'Yearly Automated Report',
                    description: 'Send report automatically every year'
                }
            },
            availableCards: this.getAvailableCards(filterListData, cardFilter),
            currentFilters: {
                transferType,
                activeFilters: this.getActiveFilters(cardFilter),
                hasFilters: this.hasActiveFilters(cardFilter)
            }
        };
    }

    /**
     * Get available cards for report generation
     * IMPORTANT: Now page-aware - returns cards based on current page
     * @param {Object} filterListData - Filter list data
     * @param {Object} cardFilter - Card filter data
     * @returns {Object} Available cards
     */
    getAvailableCards(filterListData, cardFilter) {
        const cards = {};
        const pageType = this.getCurrentPageType();
        const isCommunityPage = pageType === PAGE_TYPE.COMMUNITY_TRANSFER;
        const isDeceasedPage = pageType === PAGE_TYPE.DECEASED;
        const isOverallPage = pageType === PAGE_TYPE.OVERALL;
        const isAdmissionPage = pageType === PAGE_TYPE.ADMISSION;

        if (isOverallPage) {
            // ========== OVERALL PAGE CARDS ==========
            
            // Insurance
            if (filterListData?.insuranceData?.length > 0) {
                cards.insurance = {
                    type: OVERALL_CARDS_TYPE.INSURANCE_DATA,
                    label: OVERALL_CARDS_LABELS.insuranceData,
                    hasData: true,
                    itemCount: filterListData.insuranceData.length,
                    hasFilters: (cardFilter?.insuranceData || []).length > 0
                };
            }

            // Doctors
            if (filterListData?.doctorData?.length > 0) {
                cards.doctors = {
                    type: OVERALL_CARDS_TYPE.DOCTOR_DATA,
                    label: OVERALL_CARDS_LABELS.doctorData,
                    hasData: true,
                    itemCount: filterListData.doctorData.length,
                    hasFilters: (cardFilter?.doctorData || []).length > 0
                };
            }

            // Floors
            if (filterListData?.floorsData?.length > 0) {
                cards.floors = {
                    type: OVERALL_CARDS_TYPE.FLOORS_DATA,
                    label: OVERALL_CARDS_LABELS.floorsData,
                    hasData: true,
                    itemCount: filterListData.floorsData.length,
                    hasFilters: (cardFilter?.floorsData || []).length > 0
                };
            }

            // Days
            if (filterListData?.daysData?.length > 0) {
                cards.days = {
                    type: OVERALL_CARDS_TYPE.DAYS_DATA,
                    label: OVERALL_CARDS_LABELS.daysData,
                    hasData: true,
                    itemCount: filterListData.daysData.length,
                    hasFilters: (cardFilter?.daysData || []).length > 0
                };
            }

            // 90 Days Analysis
            if (filterListData?.ninetyDaysData?.length > 0) {
                cards.ninetyDays = {
                    type: OVERALL_CARDS_TYPE.NINETY_DAYS_DATA,
                    label: OVERALL_CARDS_LABELS.ninetyDaysData,
                    hasData: true,
                    itemCount: filterListData.ninetyDaysData.length,
                    hasFilters: (cardFilter?.ninetyDaysData || []).length > 0
                };
            }
        } else if (isAdmissionPage) {
            // ========== ADMISSION PAGE CARDS ==========
            
            // Doctors
            if (filterListData?.doctorData?.length > 0) {
                cards.doctors = {
                    type: ADMISSION_CARDS_TYPE.DOCTOR_DATA,
                    label: ADMISSION_CARDS_LABELS.doctorData,
                    hasData: true,
                    itemCount: filterListData.doctorData.length,
                    hasFilters: (cardFilter?.doctorData || []).length > 0
                };
            }

            // Days
            if (filterListData?.daysData?.length > 0) {
                cards.days = {
                    type: ADMISSION_CARDS_TYPE.DAYS_DATA,
                    label: ADMISSION_CARDS_LABELS.daysData,
                    hasData: true,
                    itemCount: filterListData.daysData.length,
                    hasFilters: (cardFilter?.daysData || []).length > 0
                };
            }

            // Diagnosis
            if (filterListData?.dxData?.length > 0) {
                cards.diagnosis = {
                    type: ADMISSION_CARDS_TYPE.DX_DATA,
                    label: ADMISSION_CARDS_LABELS.dxData,
                    hasData: true,
                    itemCount: filterListData.dxData.length,
                    hasFilters: (cardFilter?.dxData || []).length > 0
                };
            }

            // Insurance
            if (filterListData?.insuranceData?.length > 0) {
                cards.insurance = {
                    type: ADMISSION_CARDS_TYPE.INSURANCE_DATA,
                    label: ADMISSION_CARDS_LABELS.insuranceData,
                    hasData: true,
                    itemCount: filterListData.insuranceData.length,
                    hasFilters: (cardFilter?.insuranceData || []).length > 0
                };
            }

            // Floors
            if (filterListData?.floorsData?.length > 0) {
                cards.floors = {
                    type: ADMISSION_CARDS_TYPE.FLOORS_DATA,
                    label: ADMISSION_CARDS_LABELS.floorsData,
                    hasData: true,
                    itemCount: filterListData.floorsData.length,
                    hasFilters: (cardFilter?.floorsData || []).length > 0
                };
            }

            // Hospital
            if (filterListData?.hospitalData?.length > 0) {
                cards.hospitals = {
                    type: ADMISSION_CARDS_TYPE.HOSPITAL_DATA,
                    label: ADMISSION_CARDS_LABELS.hospitalData,
                    hasData: true,
                    itemCount: filterListData.hospitalData.length,
                    hasFilters: (cardFilter?.hospitalData || []).length > 0
                };
            }

            // 60 Days Analysis
            if (filterListData?.sixtyDaysData?.length > 0) {
                cards.sixtyDays = {
                    type: ADMISSION_CARDS_TYPE.SIXTY_DAYS_DATA,
                    label: '60 Days Analysis',
                    hasData: true,
                    itemCount: filterListData.sixtyDaysData.length,
                    hasFilters: (cardFilter?.sixtyDaysData || []).length > 0
                };
            }
        } else if (isCommunityPage) {
            // ========== COMMUNITY TRANSFER PAGE CARDS ==========
            
            // Get dbData for total cards (these are not in filterListData)
            const state = this.getState();
            const dbData = state.communityTransfer?.dbData || {};
            
            // Safe Discharge (Total card - from dbData)
            if (dbData.totalSafeDischarge !== undefined) {
                cards.safeDischarge = {
                    type: CO_TRANSFER_CARDS_TYPE.SAFE_DISCHARGE,
                    label: COMMUNITY_CARD_LABELS.safeDischarge,
                    hasData: true,
                    isTotalCard: true,
                    total: dbData.totalSafeDischarge
                };
            }

            // SNF (Total card - from dbData)
            if (dbData.totalSNF !== undefined) {
                cards.SNF = {
                    type: CO_TRANSFER_CARDS_TYPE.SNF,
                    label: COMMUNITY_CARD_LABELS.SNF,
                    hasData: true,
                    isTotalCard: true,
                    total: dbData.totalSNF
                };
            }

            // AMA (Total card - from dbData)
            if (dbData.totalAMA !== undefined) {
                cards.AMA = {
                    type: CO_TRANSFER_CARDS_TYPE.AMA,
                    label: COMMUNITY_CARD_LABELS.AMA,
                    hasData: true,
                    isTotalCard: true,
                    total: dbData.totalAMA
                };
            }

            // Insurance
            if (filterListData?.insuranceData?.length > 0) {
                cards.insurance = {
                    type: CO_TRANSFER_CARDS_TYPE.INSURANCE_DATA,
                    label: COMMUNITY_CARD_LABELS.insuranceData,
                    hasData: true,
                    itemCount: filterListData.insuranceData.length,
                    hasFilters: (cardFilter?.insuranceData || []).length > 0
                };
            }

            // Returns
            if (filterListData?.returnsData?.length > 0) {
                cards.returns = {
                    type: CO_TRANSFER_CARDS_TYPE.RETURNS_DATA,
                    label: COMMUNITY_CARD_LABELS.returnsData,
                    hasData: true,
                    itemCount: filterListData.returnsData.length,
                    hasFilters: (cardFilter?.returnsData || []).length > 0
                };
            }

            // 90 Days (sixtyDaysData in community page)
            if (filterListData?.sixtyDaysData?.length > 0) {
                cards.ninetyDays = {
                    type: CO_TRANSFER_CARDS_TYPE.SIXTY_DAYS_DATA,
                    label: COMMUNITY_CARD_LABELS.sixtyDaysData,
                    hasData: true,
                    itemCount: filterListData.sixtyDaysData.length,
                    hasFilters: (cardFilter?.sixtyDaysData || []).length > 0
                };
            }

            // Floors
            if (filterListData?.floorsData?.length > 0) {
                cards.floors = {
                    type: CO_TRANSFER_CARDS_TYPE.FLOORS_DATA,
                    label: COMMUNITY_CARD_LABELS.floorsData,
                    hasData: true,
                    itemCount: filterListData.floorsData.length,
                    hasFilters: (cardFilter?.floorsData || []).length > 0
                };
            }

            // Doctors
            if (filterListData?.doctorData?.length > 0) {
                cards.doctors = {
                    type: CO_TRANSFER_CARDS_TYPE.DOCTOR_DATA,
                    label: COMMUNITY_CARD_LABELS.doctorData,
                    hasData: true,
                    itemCount: filterListData.doctorData.length,
                    hasFilters: (cardFilter?.doctorData || []).length > 0
                };
            }

            // Days
            if (filterListData?.daysData?.length > 0) {
                cards.days = {
                    type: CO_TRANSFER_CARDS_TYPE.DAYS_DATA,
                    label: COMMUNITY_CARD_LABELS.daysData,
                    hasData: true,
                    itemCount: filterListData.daysData.length,
                    hasFilters: (cardFilter?.daysData || []).length > 0
                };
            }

            // Safe Discharge Assisted Living
            if (filterListData?.safeDischargeAssLivData?.length > 0) {
                cards.safeDischargeAssLiv = {
                    type: CO_TRANSFER_CARDS_TYPE.SAFE_DISCHARGE_ASS_LIV_DATA,
                    label: COMMUNITY_CARD_LABELS.safeDischargeAssLivData,
                    hasData: true,
                    itemCount: filterListData.safeDischargeAssLivData.length,
                    hasFilters: (cardFilter?.safeDischargeAssLivData || []).length > 0
                };
            }

            // SNF Facility
            if (filterListData?.snfFacilityData?.length > 0) {
                cards.snfFacility = {
                    type: CO_TRANSFER_CARDS_TYPE.SNF_FACILITY_DATA,
                    label: COMMUNITY_CARD_LABELS.snfFacilityData,
                    hasData: true,
                    itemCount: filterListData.snfFacilityData.length,
                    hasFilters: (cardFilter?.snfFacilityData || []).length > 0
                };
            }
        } else if (isDeceasedPage) {
            // ========== DECEASED PAGE CARDS ==========
            
            // Insurance
            if (filterListData?.insuranceData?.length > 0) {
                cards.insurance = {
                    type: DECEASED_CARDS_TYPE.INSURANCE_DATA,
                    label: DECEASED_CARDS_LABELS.insuranceData,
                    hasData: true,
                    itemCount: filterListData.insuranceData.length,
                    hasFilters: (cardFilter?.insuranceData || []).length > 0
                };
            }

            // Doctor
            if (filterListData?.doctorData?.length > 0) {
                cards.doctors = {
                    type: DECEASED_CARDS_TYPE.DOCTOR_DATA,
                    label: DECEASED_CARDS_LABELS.doctorData,
                    hasData: true,
                    itemCount: filterListData.doctorData.length,
                    hasFilters: (cardFilter?.doctorData || []).length > 0
                };
            }

            // Floors
            if (filterListData?.floorsData?.length > 0) {
                cards.floors = {
                    type: DECEASED_CARDS_TYPE.FLOORS_DATA,
                    label: DECEASED_CARDS_LABELS.floorsData,
                    hasData: true,
                    itemCount: filterListData.floorsData.length,
                    hasFilters: (cardFilter?.floorsData || []).length > 0
                };
            }

            // Days
            if (filterListData?.daysData?.length > 0) {
                cards.days = {
                    type: DECEASED_CARDS_TYPE.DAYS_DATA,
                    label: DECEASED_CARDS_LABELS.daysData,
                    hasData: true,
                    itemCount: filterListData.daysData.length,
                    hasFilters: (cardFilter?.daysData || []).length > 0
                };
            }

            // 90 Days Analysis
            if (filterListData?.ninetyDaysData?.length > 0) {
                cards.ninetyDays = {
                    type: DECEASED_CARDS_TYPE.NINETY_DAYS_DATA,
                    label: DECEASED_CARDS_LABELS.ninetyDaysData,
                    hasData: true,
                    itemCount: filterListData.ninetyDaysData.length,
                    hasFilters: (cardFilter?.ninetyDaysData || []).length > 0
                };
            }
        } else {
            // ========== HOSPITAL PAGE CARDS ==========
            
            // Check each card type
            if (filterListData?.hospitalizations?.length > 0) {
                cards.hospitalizations = {
                    type: HOSPITAL_CARDS_TYPE.HOSPITALIZATIONS,
                    label: 'New Hospitalizations & Re-Hospitalizations',
                    hasData: true,
                    itemCount: filterListData.hospitalizations.length,
                    hasFilters: (cardFilter?.hospitalizations || []).length > 0
                };
            }

            if (filterListData?.DCERData?.length > 0) {
                cards.dcer = {
                    type: HOSPITAL_CARDS_TYPE.DCER_DATA,
                    label: 'DC / ER',
                    hasData: true,
                    itemCount: filterListData.DCERData.length,
                    hasFilters: (cardFilter?.DCERData || []).length > 0
                };
            }

            if (filterListData?.insuranceData?.length > 0) {
                cards.insurance = {
                    type: HOSPITAL_CARDS_TYPE.INSURANCE_DATA,
                    label: HOSPITAL_CARDS_LABELS.insuranceData,
                    hasData: true,
                    itemCount: filterListData.insuranceData.length,
                    hasFilters: (cardFilter?.insuranceData || []).length > 0
                };
            }

            if (filterListData?.returnsData?.length > 0) {
                cards.returns = {
                    type: HOSPITAL_CARDS_TYPE.RETURNS_DATA,
                    label: 'Returned / Didn\'t Return',
                    hasData: true,
                    itemCount: filterListData.returnsData.length,
                    hasFilters: (cardFilter?.returnsData || []).length > 0
                };
            }

            if (filterListData?.ninetyDaysData?.length > 0) {
                cards.ninetyDays = {
                    type: HOSPITAL_CARDS_TYPE.NINETY_DAYS_DATA,
                    label: '90 Days Analysis',
                    hasData: true,
                    itemCount: filterListData.ninetyDaysData.length,
                    hasFilters: (cardFilter?.ninetyDaysData || []).length > 0
                };
            }

            if (filterListData?.floorsData?.length > 0) {
                cards.floors = {
                    type: HOSPITAL_CARDS_TYPE.FLOORS_DATA,
                    label: HOSPITAL_CARDS_LABELS.floorsData,
                    hasData: true,
                    itemCount: filterListData.floorsData.length,
                    hasFilters: (cardFilter?.floorsData || []).length > 0
                };
            }

            if (filterListData?.doctorData?.length > 0) {
                cards.doctors = {
                    type: HOSPITAL_CARDS_TYPE.DOCTOR_DATA,
                    label: HOSPITAL_CARDS_LABELS.doctorData,
                    hasData: true,
                    itemCount: filterListData.doctorData.length,
                    hasFilters: (cardFilter?.doctorData || []).length > 0
                };
            }

            if (filterListData?.daysData?.length > 0) {
                cards.days = {
                    type: HOSPITAL_CARDS_TYPE.DAYS_DATA,
                    label: HOSPITAL_CARDS_LABELS.daysData,
                    hasData: true,
                    itemCount: filterListData.daysData.length,
                    hasFilters: (cardFilter?.daysData || []).length > 0
                };
            }

            if (filterListData?.dxData?.length > 0) {
                cards.diagnosis = {
                    type: HOSPITAL_CARDS_TYPE.DX_DATA,
                    label: 'Per Diagnosis',
                    hasData: true,
                    itemCount: filterListData.dxData.length,
                    hasFilters: (cardFilter?.dxData || []).length > 0
                };
            }

            if (filterListData?.shiftData?.length > 0) {
                cards.shifts = {
                    type: HOSPITAL_CARDS_TYPE.SHIFT_DATA,
                    label: 'Per Shift',
                    hasData: true,
                    itemCount: filterListData.shiftData.length,
                    hasFilters: (cardFilter?.shiftData || []).length > 0
                };
            }

            if (filterListData?.nurseData?.length > 0) {
                cards.nurses = {
                    type: HOSPITAL_CARDS_TYPE.NURSE_DATA,
                    label: 'Per Nurse',
                    hasData: true,
                    itemCount: filterListData.nurseData.length,
                    hasFilters: (cardFilter?.nurseData || []).length > 0
                };
            }

            if (filterListData?.hospitalData?.length > 0) {
                cards.hospitals = {
                    type: HOSPITAL_CARDS_TYPE.HOSPITAL_DATA,
                    label: 'Hospitals residents transferred to',
                    hasData: true,
                    itemCount: filterListData.hospitalData.length,
                    hasFilters: (cardFilter?.hospitalData || []).length > 0
                };
            }
        }

        // CRITICAL: Add custom/dynamic cards (same for all pages)
        const state = this.getState();
        const pageState = isCommunityPage ? state.communityTransfer : 
                         isDeceasedPage ? state.deceased : 
                         isOverallPage ? state.overall :
                         isAdmissionPage ? state.admission :
                         state.hospital;
        const dynamicCards = pageState?.dynamicCards || [];
        const customTabs = pageState?.patientList?.customTabs || [];
        
        // Add dynamic cards (question-based custom cards)
        dynamicCards.forEach(card => {
            const accessor = card.accessor;
            const dataList = filterListData?.[accessor];
            if (dataList && dataList.length > 0) {
                cards[accessor] = {
                    type: accessor, // Use accessor as type
                    label: card.label || card.title || card.tableLabel,
                    hasData: true,
                    itemCount: dataList.length,
                    hasFilters: (cardFilter?.[accessor] || []).length > 0,
                    isCustomCard: true,
                    isCustomTab: false,
                    fullCardData: card
                };
            }
        });
        
        // Add custom tabs (filter-based custom cards)
        customTabs.forEach(tab => {
            const accessor = tab.accessor;
            const dataList = filterListData?.[accessor];
            if (dataList && dataList.length > 0) {
                cards[accessor] = {
                    type: accessor, // Use accessor as type
                    label: tab.label || tab.title,
                    hasData: true,
                    itemCount: dataList.length,
                    hasFilters: (cardFilter?.[accessor] || []).length > 0,
                    isCustomCard: false,
                    isCustomTab: true,
                    fullCardData: tab
                };
            }
        });

        return cards;
    }

    /**
     * Get active filters
     * @param {Object} cardFilter - Card filter data
     * @returns {Object} Active filters
     */
    getActiveFilters(cardFilter) {
        const activeFilters = {};
        
        Object.entries(cardFilter || {}).forEach(([key, value]) => {
            if (Array.isArray(value) && value.length > 0) {
                activeFilters[key] = value;
            }
        });

        return activeFilters;
    }

    /**
     * Check if there are any active filters
     * @param {Object} cardFilter - Card filter data
     * @returns {boolean} Has active filters
     */
    hasActiveFilters(cardFilter) {
        return Object.values(cardFilter || {}).some(filter => 
            Array.isArray(filter) && filter.length > 0
        );
    }

    /**
     * Generate a PDF report for a specific card
     * @param {string} cardType - Card type (e.g., 'hospitalizations', 'dcer')
     * @param {string} customTitle - Custom title for the report
     * @param {Object} customCardMetadata - Custom card metadata (for dynamic/custom cards)
     * @returns {Promise} Report generation result
     */
    async generatePdfReport(cardType, customTitle = null, customCardMetadata = null) {
        if (!this.handleOnClickReport || !this.getState) {
            throw new Error('Service not initialized. Call initialize() first.');
        }

        // CRITICAL: If customCardMetadata is provided, use it directly (for custom cards)
        if (customCardMetadata) {
            logger.log('✅ Using custom card metadata path - UPDATED CODE LOADED!', {
                customCardMetadata,
                cardType,
                customTitle
            });
            
            const title = customTitle || customCardMetadata.cardTitle || cardType;
            const customData = {
                questionId: customCardMetadata.questionId,
                isCustomCard: customCardMetadata.isCustom !== undefined ? customCardMetadata.isCustom : true,
                isCustomTab: customCardMetadata.isCustomTab !== undefined ? customCardMetadata.isCustomTab : false,
                cardTitle: title
            };
            
            logger.log('🎯 Custom card report data prepared (from metadata):', customData);
            
            try {
                // Pass customData as the 5th parameter to handleOnClickReport
                await this.handleOnClickReport(cardType, title, false, null, customData);
                return {
                    success: true,
                    message: `PDF report generated successfully for ${title}`,
                    cardType: cardType,
                    title
                };
            } catch (error) {
                logger.error('Error generating PDF report:', error);
                throw new Error(`Failed to generate PDF report: ${error.message}`);
            }
        }

        // Standard card lookup (for regular cards)
        const state = this.getState();
        const pageType = this.getCurrentPageType();
        const isCommunityPage = pageType === PAGE_TYPE.COMMUNITY_TRANSFER;
        const isDeceasedPage = pageType === PAGE_TYPE.DECEASED;
        const isOverallPage = pageType === PAGE_TYPE.OVERALL;
        const isAdmissionPage = pageType === PAGE_TYPE.ADMISSION;
        
        // Get data from correct state based on page type
        const pageState = isCommunityPage ? state.communityTransfer : 
                         isDeceasedPage ? state.deceased : 
                         isOverallPage ? state.overall :
                         isAdmissionPage ? state.admission :
                         state.hospital;
        const { filterListData, cardFilter } = pageState || {};
        const cards = this.getAvailableCards(filterListData, cardFilter);
        const cardInfo = cards[cardType];
        
        logger.log('🔍 [generatePdfReport] Card lookup:', {
            cardType,
            pageType,
            isCommunityPage,
            isDeceasedPage,
            hasPageState: !!pageState,
            hasFilterListData: !!filterListData,
            foundCard: !!cardInfo,
            availableCards: Object.keys(cards)
        });
        
        if (!cardInfo) {
            throw new Error(`Card type '${cardType}' not found or has no data`);
        }

        const title = customTitle || cardInfo.label;
        
        // CRITICAL: For Community Transfer total cards, we need to pass the correct type
        // Community total cards use "totalAMA", "totalSNF", "totalSafeDischarge"
        // Regular cards use their cardType directly
        let reportType = cardInfo.type;
        let isTotalCard = false;
        
        if (isCommunityPage && cardInfo.isTotalCard) {
            // Map card types to report types for Community Transfer total cards
            const communityTotalCardTypeMap = {
                [CO_TRANSFER_CARDS_TYPE.AMA]: 'totalAMA',
                [CO_TRANSFER_CARDS_TYPE.SNF]: 'totalSNF',
                [CO_TRANSFER_CARDS_TYPE.SAFE_DISCHARGE]: 'totalSafeDischarge'
            };
            reportType = communityTotalCardTypeMap[cardInfo.type] || cardInfo.type;
            isTotalCard = true;
            
            logger.log('🎯 Community total card report type mapping:', {
                originalType: cardInfo.type,
                mappedType: reportType,
                isTotalCard
            });
        }
        
        // Build custom data object if this is a custom card
        let customData = null;
        if (cardInfo.isCustomCard || cardInfo.isCustomTab) {
            customData = {
                questionId: cardInfo.fullCardData?._id,
                isCustomCard: cardInfo.isCustomCard,
                isCustomTab: cardInfo.isCustomTab,
                cardTitle: title
            };
            logger.log('🎯 Custom card report data prepared (from lookup):', customData);
        }
        
        try {
            // Pass customData as the 5th parameter to handleOnClickReport
            await this.handleOnClickReport(reportType, title, isTotalCard, null, customData);
            return {
                success: true,
                message: `PDF report generated successfully for ${title}`,
                cardType: cardInfo.type,
                title
            };
        } catch (error) {
            logger.error('Error generating PDF report:', error);
            throw new Error(`Failed to generate PDF report: ${error.message}`);
        }
    }

    /**
     * Generate an Excel report for a specific card
     * @param {string} cardType - Card type
     * @param {string} customTitle - Custom title for the report
     * @param {Object} customCardMetadata - Custom card metadata (for dynamic/custom cards)
     * @returns {Promise} Report generation result
     */
    async generateExcelReport(cardType, customTitle = null, customCardMetadata = null) {
        if (!this.downloadExcel || !this.getState) {
            throw new Error('Service not initialized. Call initialize() first.');
        }

        const state = this.getState();
        const pageType = this.getCurrentPageType();
        const isCommunityPage = pageType === PAGE_TYPE.COMMUNITY_TRANSFER;
        const isDeceasedPage = pageType === PAGE_TYPE.DECEASED;
        const isOverallPage = pageType === PAGE_TYPE.OVERALL;
        const isAdmissionPage = pageType === PAGE_TYPE.ADMISSION;
        
        // Get data from correct state based on page type
        const pageState = isCommunityPage ? state.communityTransfer : 
                         isDeceasedPage ? state.deceased : 
                         isOverallPage ? state.overall :
                         isAdmissionPage ? state.admission :
                         state.hospital;
        const { filterListData, cardFilter } = pageState || {};
        const cards = this.getAvailableCards(filterListData, cardFilter);
        const cardInfo = cards[cardType];
        
        logger.log('🔍 [generateExcelReport] Card lookup:', {
            cardType,
            pageType,
            isCommunityPage,
            isDeceasedPage,
            hasPageState: !!pageState,
            hasFilterListData: !!filterListData,
            foundCard: !!cardInfo,
            availableCards: Object.keys(cards)
        });
        
        if (!cardInfo) {
            throw new Error(`Card type '${cardType}' not found or has no data`);
        }

        // Note: Excel report generation currently doesn't support custom cards fully
        // This parameter is included for consistency and future enhancement
        logger.log('🎯 Generating Excel report:', {
            cardType,
            isCustomCard: cardInfo.isCustomCard,
            hasCustomCardMetadata: !!customCardMetadata
        });

        try {
            await this.downloadExcel();
            return {
                success: true,
                message: `Excel report generated successfully for ${cardInfo.label}`,
                cardType: cardInfo.type,
                title: customTitle || cardInfo.label
            };
        } catch (error) {
            logger.error('Error generating Excel report:', error);
            throw new Error(`Failed to generate Excel report: ${error.message}`);
        }
    }

    /**
     * Set up an automated report
     * @param {string} cardType - Card type
     * @param {string} reportType - Report type (daily, weekly, etc.)
     * @param {string} fileType - File type (pdf, excel)
     * @param {string} reportName - Custom report name
     * @param {Object} options - Additional options (can include customCardMetadata)
     * @returns {Promise} Automated report setup result
     */
    async setupAutomatedReport(cardType, reportType, fileType, reportName, options = {}) {
        if (!this.handleOnClickReport || !this.getState) {
            throw new Error('Service not initialized. Call initialize() first.');
        }

        // CRITICAL: Map cardType to display title (page-aware)
        const pageType = this.getCurrentPageType();
        const isCommunityPage = pageType === PAGE_TYPE.COMMUNITY_TRANSFER;
        const isDeceasedPage = pageType === PAGE_TYPE.DECEASED;
        const isOverallPage = pageType === PAGE_TYPE.OVERALL;
        const isAdmissionPage = pageType === PAGE_TYPE.ADMISSION;
        
        let cardTitles = {};
        if (isCommunityPage) {
            // Community Transfer card titles
            cardTitles = {
                'AMA': COMMUNITY_CARD_LABELS.AMA,
                'SNF': COMMUNITY_CARD_LABELS.SNF,
                'safeDischarge': COMMUNITY_CARD_LABELS.safeDischarge,
                'insuranceData': COMMUNITY_CARD_LABELS.insuranceData,
                'returnsData': COMMUNITY_CARD_LABELS.returnsData,
                'sixtyDaysData': COMMUNITY_CARD_LABELS.sixtyDaysData,
                'floorsData': COMMUNITY_CARD_LABELS.floorsData,
                'doctorData': COMMUNITY_CARD_LABELS.doctorData,
                'daysData': COMMUNITY_CARD_LABELS.daysData,
                'safeDischargeAssLivData': COMMUNITY_CARD_LABELS.safeDischargeAssLivData,
                'snfFacilityData': COMMUNITY_CARD_LABELS.snfFacilityData,
                'total': COMMUNITY_CARD_LABELS.totalMain
            };
        } else if (isDeceasedPage) {
            // Deceased page card titles
            cardTitles = {
                'insuranceData': DECEASED_CARDS_LABELS.insuranceData,
                'doctorData': DECEASED_CARDS_LABELS.doctorData,
                'floorsData': DECEASED_CARDS_LABELS.floorsData,
                'daysData': DECEASED_CARDS_LABELS.daysData,
                'ninetyDaysData': DECEASED_CARDS_LABELS.ninetyDaysData,
                'total': DECEASED_CARDS_LABELS.total
            };
        } else if (isOverallPage) {
            // Overall page card titles
            cardTitles = {
                'insuranceData': OVERALL_CARDS_LABELS.insuranceData,
                'doctorData': OVERALL_CARDS_LABELS.doctorData,
                'floorsData': OVERALL_CARDS_LABELS.floorsData,
                'daysData': OVERALL_CARDS_LABELS.daysData,
                'ninetyDaysData': OVERALL_CARDS_LABELS.ninetyDaysData
            };
        } else if (isAdmissionPage) {
            // Admission page card titles
            cardTitles = {
                'doctorData': ADMISSION_CARDS_LABELS.doctorData,
                'daysData': ADMISSION_CARDS_LABELS.daysData,
                'dxData': ADMISSION_CARDS_LABELS.dxData,
                'insuranceData': ADMISSION_CARDS_LABELS.insuranceData,
                'floorsData': ADMISSION_CARDS_LABELS.floorsData,
                'hospitalData': ADMISSION_CARDS_LABELS.hospitalData,
                'sixtyDaysData': '60 Days Analysis',
                'admission': ADMISSION_CARDS_LABELS.admission,
                'readmission': ADMISSION_CARDS_LABELS.readmission,
                'total': ADMISSION_CARDS_LABELS.total
            };
        } else {
            // Hospital page card titles
            cardTitles = {
                'DCERData': 'DC/ER',
                'hospitalizations': 'Hospitalizations',
                'insuranceData': 'Insurance',
                'returnsData': 'Returns',
                'ninetyDaysData': '90 Days',
                'floorsData': 'Floors',
                'doctorData': 'Doctors',
                'daysData': 'Days',
                'dxData': 'Diagnosis',
                'shiftData': 'Shifts',
                'nurseData': 'Nurses',
                'hospitalData': 'Hospitals',
                'unplannedHospitalTransfer': 'Unplanned Transfer',
                'plannedHospitalTransfer': 'Planned Transfer',
                'total': 'Total'
            };
        }

        const automaticallyReport = {
            type: reportType,
            reportFileType: fileType,
            name: reportName,
            cardType: cardType,  // ✅ CRITICAL: Include cardType so ReportSummary knows which card to display
            title: cardTitles[cardType] || cardType,  // ✅ CRITICAL: Include title for ReportSummary description
            ...options
        };

        // CRITICAL: If customCardMetadata is provided, use it directly (for custom cards)
        if (options.customCardMetadata) {
            const metadata = options.customCardMetadata;
            const title = metadata.cardTitle || cardType;
            const customData = {
                questionId: metadata.questionId,
                isCustomCard: metadata.isCustom !== undefined ? metadata.isCustom : true,
                isCustomTab: metadata.isCustomTab !== undefined ? metadata.isCustomTab : false,
                cardTitle: title
            };
            
            logger.log('🎯 Custom card automated report data prepared (from metadata):', customData);
            
            try {
                const result = await this.handleOnClickReport(
                    cardType, 
                    title, 
                    false, 
                    automaticallyReport,
                    customData
                );
                
                return {
                    success: true,
                    message: `Automated ${reportType} ${fileType} report set up successfully for ${title}`,
                    cardType: cardType,
                    reportName,
                    reportType,
                    fileType,
                    result
                };
            } catch (error) {
                logger.error('Error setting up automated report:', error);
                throw new Error(`Failed to set up automated report: ${error.message}`);
            }
        }

        // Standard card lookup (for regular cards)
        const state = this.getState();
        // pageType, isCommunityPage, isDeceasedPage, isOverallPage, isAdmissionPage already declared at the beginning of this function
        
        // Get data from correct state based on page type
        const pageState = isCommunityPage ? state.communityTransfer : 
                         isDeceasedPage ? state.deceased : 
                         isOverallPage ? state.overall :
                         isAdmissionPage ? state.admission :
                         state.hospital;
        const { filterListData, cardFilter } = pageState || {};
        const cards = this.getAvailableCards(filterListData, cardFilter);
        const cardInfo = cards[cardType];
        
        logger.log('🔍 [setupAutomatedReport] Card lookup:', {
            cardType,
            pageType,
            isCommunityPage,
            isDeceasedPage,
            hasPageState: !!pageState,
            foundCard: !!cardInfo,
            availableCards: Object.keys(cards)
        });
        
        if (!cardInfo) {
            throw new Error(`Card type '${cardType}' not found or has no data`);
        }

        // CRITICAL: For Community Transfer total cards, map to report types
        let reportCardType = cardInfo.type;
        let isTotalCard = false;
        
        if (isCommunityPage && cardInfo.isTotalCard) {
            const communityTotalCardTypeMap = {
                [CO_TRANSFER_CARDS_TYPE.AMA]: 'totalAMA',
                [CO_TRANSFER_CARDS_TYPE.SNF]: 'totalSNF',
                [CO_TRANSFER_CARDS_TYPE.SAFE_DISCHARGE]: 'totalSafeDischarge'
            };
            reportCardType = communityTotalCardTypeMap[cardInfo.type] || cardInfo.type;
            isTotalCard = true;
            
            logger.log('🎯 Community total card automated report type mapping:', {
                originalType: cardInfo.type,
                mappedType: reportCardType,
                isTotalCard
            });
        }

        // Build custom data object if this is a custom card
        let customData = null;
        if (cardInfo.isCustomCard || cardInfo.isCustomTab) {
            customData = {
                questionId: cardInfo.fullCardData?._id,
                isCustomCard: cardInfo.isCustomCard,
                isCustomTab: cardInfo.isCustomTab,
                cardTitle: cardInfo.label
            };
            logger.log('🎯 Custom card automated report data prepared (from lookup):', customData);
        }

        try {
            // Pass customData as the 5th parameter to handleOnClickReport
            const result = await this.handleOnClickReport(
                reportCardType, 
                cardInfo.label, 
                isTotalCard, 
                automaticallyReport,
                customData
            );
            
            return {
                success: true,
                message: `Automated ${reportType} ${fileType} report set up successfully for ${cardInfo.label}`,
                cardType: cardInfo.type,
                reportName,
                reportType,
                fileType,
                result
            };
        } catch (error) {
            logger.error('Error setting up automated report:', error);
            throw new Error(`Failed to set up automated report: ${error.message}`);
        }
    }

    /**
     * Get AI action suggestions for reports
     * IMPORTANT: Now page-aware - returns suggestions based on current page
     * @returns {Array} AI action suggestions
     */
    getAIActionSuggestions() {
        if (!this.getState) {
            return [];
        }

        const state = this.getState();
        const pageType = this.getCurrentPageType();
        const isCommunityPage = pageType === PAGE_TYPE.COMMUNITY_TRANSFER;
        const isDeceasedPage = pageType === PAGE_TYPE.DECEASED;
        const isOverallPage = pageType === PAGE_TYPE.OVERALL;
        const isAdmissionPage = pageType === PAGE_TYPE.ADMISSION;
        
        // Get data from correct state based on page type
        const pageState = isCommunityPage ? state.communityTransfer : 
                         isDeceasedPage ? state.deceased : 
                         isOverallPage ? state.overall :
                         isAdmissionPage ? state.admission :
                         state.hospital;
        const { filterListData, cardFilter } = pageState || {};
        const cards = this.getAvailableCards(filterListData, cardFilter);
        const suggestions = [];

        // Basic report generation suggestions
        Object.entries(cards).forEach(([key, card]) => {
            suggestions.push({
                action: 'generatePdfReport',
                description: `Generate PDF report for ${card.label}`,
                parameters: { cardType: key },
                example: `Generate a PDF report for ${card.label.toLowerCase()}`
            });

            suggestions.push({
                action: 'generateExcelReport',
                description: `Generate Excel report for ${card.label}`,
                parameters: { cardType: key },
                example: `Generate an Excel report for ${card.label.toLowerCase()}`
            });
        });

        // Automated report suggestions
        const automatedTypes = ['daily', 'weekly', 'monthly'];
        const fileTypes = ['pdf', 'excel'];

        automatedTypes.forEach(reportType => {
            fileTypes.forEach(fileType => {
                suggestions.push({
                    action: 'setupAutomatedReport',
                    description: `Set up ${reportType} ${fileType} automated report`,
                    parameters: { 
                        reportType: reportType.toUpperCase(),
                        fileType: fileType.toUpperCase()
                    },
                    example: `Set up a ${reportType} ${fileType} automated report for hospitalizations`
                });
            });
        });

        return suggestions;
    }

    /**
     * Find card type by name or alias
     * @param {string} cardName - Card name or alias
     * @returns {string|null} Card type key
     */
    findCardTypeByName(cardName) {
        const lowerName = cardName.toLowerCase().trim();
        
        if (!this.getState) {
            return null;
        }

        const state = this.getState();
        const pageType = this.getCurrentPageType();
        const isCommunityPage = pageType === PAGE_TYPE.COMMUNITY_TRANSFER;
        const isDeceasedPage = pageType === PAGE_TYPE.DECEASED;
        const isOverallPage = pageType === PAGE_TYPE.OVERALL;
        const isAdmissionPage = pageType === PAGE_TYPE.ADMISSION;
        const pageState = isCommunityPage ? state.communityTransfer : 
                         isDeceasedPage ? state.deceased : 
                         isOverallPage ? state.overall :
                         isAdmissionPage ? state.admission :
                         state.hospital;
        const { filterListData, cardFilter } = pageState || {};
        const cards = this.getAvailableCards(filterListData, cardFilter);

        // Direct key match
        if (cards[lowerName]) {
            return lowerName;
        }

        // Alias matching
        const aliases = {
            'hospitalizations': ['hospitalization', 'new hospitalizations', 're-hospitalizations', 'rehospitalizations'],
            'dcer': ['dc', 'er', 'discharge', 'emergency', 'dc/er', 'dc er'],
            'insurance': ['insurance', 'insurances', 'payer', 'payers'],
            'returns': ['returns', 'return', 'returned', 'didnt return', "didn't return"],
            'ninetyDays': ['90 days', 'ninety days', '90-days', 'length of stay', 'los'],
            'floors': ['floors', 'floor', 'unit', 'units'],
            'doctors': ['doctors', 'doctor', 'physician', 'physicians', 'md'],
            'days': ['days', 'day', 'weekday', 'weekdays'],
            'diagnosis': ['diagnosis', 'diagnoses', 'dx', 'condition', 'conditions'],
            'shifts': ['shifts', 'shift', 'morning', 'evening', 'night'],
            'nurses': ['nurses', 'nurse', 'nursing', 'rn', 'lpn'],
            'hospitals': ['hospitals', 'hospital', 'facility', 'facilities']
        };

        for (const [cardType, cardAliases] of Object.entries(aliases)) {
            if (cardAliases.some(alias => 
                lowerName.includes(alias) || alias.includes(lowerName)
            )) {
                return cardType;
            }
        }

        return null;
    }

    /**
     * Generate a report based on natural language input
     * @param {string} input - Natural language input
     * @returns {Promise} Report generation result
     */
    async generateReportFromInput(input) {
        const lowerInput = input.toLowerCase();
        
        // Determine report type
        let reportType = 'pdf'; // default
        if (lowerInput.includes('excel') || lowerInput.includes('spreadsheet')) {
            reportType = 'excel';
        }

        // Determine card type
        let cardType = null;
        const words = lowerInput.split(/\s+/);
        
        for (const word of words) {
            const foundCardType = this.findCardTypeByName(word);
            if (foundCardType) {
                cardType = foundCardType;
                break;
            }
        }

        // If no specific card mentioned, generate for all cards
        if (!cardType) {
            cardType = 'hospitalizations'; // default to first available
        }

        // Generate the report
        if (reportType === 'excel') {
            return await this.generateExcelReport(cardType);
        } else {
            return await this.generatePdfReport(cardType);
        }
    }

    /**
     * Set up automated report from natural language input
     * @param {string} input - Natural language input
     * @param {string} reportName - Report name
     * @returns {Promise} Automated report setup result
     */
    async setupAutomatedReportFromInput(input, reportName) {
        const lowerInput = input.toLowerCase();
        
        // Determine frequency
        let frequency = AUTOMATICALLY_REPORT_TYPE.DAILY; // default
        if (lowerInput.includes('weekly') || lowerInput.includes('week')) {
            frequency = AUTOMATICALLY_REPORT_TYPE.WEEKLY;
        } else if (lowerInput.includes('monthly') || lowerInput.includes('month')) {
            frequency = AUTOMATICALLY_REPORT_TYPE.MONTHLY;
        } else if (lowerInput.includes('quarterly') || lowerInput.includes('quarter')) {
            frequency = AUTOMATICALLY_REPORT_TYPE.QUARTERLY;
        } else if (lowerInput.includes('yearly') || lowerInput.includes('year')) {
            frequency = AUTOMATICALLY_REPORT_TYPE.YEARLY;
        }

        // Determine file type
        let fileType = REPORT_FILE_TYPE.PDF; // default
        if (lowerInput.includes('excel') || lowerInput.includes('spreadsheet')) {
            fileType = REPORT_FILE_TYPE.EXCEL;
        }

        // Determine card type
        let cardType = this.findCardTypeByName(input) || 'hospitalizations';

        return await this.setupAutomatedReport(cardType, frequency, fileType, reportName);
    }

    /**
     * Get current report status and available actions
     * @returns {Object} Current status
     */
    getCurrentReportStatus() {
        if (!this.getState) {
            return {
                availableCards: [],
                totalCards: 0,
                hasData: false,
                currentFilters: {},
                canGenerateReports: false,
                reportTypes: [],
                automatedTypes: []
            };
        }

        const options = this.getAvailableReportOptions();
        
        return {
            availableCards: Object.keys(options.availableCards),
            totalCards: Object.keys(options.availableCards).length,
            hasData: Object.keys(options.availableCards).length > 0,
            currentFilters: options.currentFilters,
            canGenerateReports: Object.keys(options.availableCards).length > 0,
            reportTypes: Object.keys(options.reportTypes),
            automatedTypes: Object.keys(options.automatedReportTypes)
        };
    }
}

// Export singleton instance
const aiReportActionsService = new AiReportActionsService();
export default aiReportActionsService;
